/**
 * WEBAPP-14: set_type canonici per schede palestra (allineamento API / UI).
 * REST-PAUSE escluso - non è un set_type selezionabile.
 */

export const GYM_WORKOUT_SET_TYPE_OPTIONS = [
    { value: 'normal', label: 'Normale' },
    { value: 'superset', label: 'Superset' },
    { value: 'dropset', label: 'Dropset' },
    { value: 'circuit', label: 'Circuit' },
    { value: 'giant_set', label: 'Giant Set' },
    { value: 'piramidale', label: 'Piramidale' },
];

const BADGE_CLASS_BY_TYPE = {
    superset: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    dropset: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    circuit: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    giant_set: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    piramidale: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
};

export function getWorkoutSetTypeLabel(setType) {
    const opt = GYM_WORKOUT_SET_TYPE_OPTIONS.find((o) => o.value === setType);
    return opt?.label ?? setType;
}

export function getWorkoutSetTypeBadgeClass(setType) {
    return BADGE_CLASS_BY_TYPE[setType] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
}

const GROUP_BORDER_CLASS_BY_TYPE = {
    superset: 'border-l-blue-500',
    dropset: 'border-l-red-500',
    circuit: 'border-l-green-500',
    giant_set: 'border-l-purple-500',
};

/** Barra laterale del gruppo per le liste con divide-y (che sovrascrive il colore dei bordi). */
const GROUP_BAR_CLASS_BY_TYPE = {
    superset: 'bg-blue-500',
    dropset: 'bg-red-500',
    circuit: 'bg-green-500',
    giant_set: 'bg-purple-500',
};

/** Tipi che si collegano da soli all'esercizio sopra quando li scegli. */
const AUTO_LINK_SET_TYPES = ['superset', 'circuit', 'giant_set'];

/** Tipi che non possono mai far parte di un gruppo collegato. */
const UNLINKABLE_SET_TYPES = ['normal', 'piramidale'];

/** L'API può restituire il flag come boolean, numero o stringa ("0"/"1"). */
function isLinked(exercise) {
    const v = exercise?.linked_to_previous;
    return v === true || v === 1 || v === '1';
}

function canLinkToPrevious(list, index) {
    const exercise = list[index];
    const previous = list[index - 1];
    return index > 0
        && Boolean(exercise?.set_type)
        && !UNLINKABLE_SET_TYPES.includes(exercise.set_type)
        && previous?.set_type === exercise.set_type;
}

/** Il superset va a coppie: non si aggancia a un superset che ha già il suo compagno. */
function shouldAutoLink(list, index) {
    if (!canLinkToPrevious(list, index) || !AUTO_LINK_SET_TYPES.includes(list[index].set_type)) {
        return false;
    }
    return list[index].set_type !== 'superset' || !isLinked(list[index - 1]);
}

/**
 * Toglie i collegamenti non più validi (primo esercizio, tipo diverso da quello sopra,
 * normale/piramidale). Da usare dopo ogni modifica o riordino della lista.
 */
export function normalizeWorkoutLinks(list) {
    let changed = false;
    const result = list.map((exercise, index) => {
        if (isLinked(exercise) && !canLinkToPrevious(list, index)) {
            changed = true;
            return { ...exercise, linked_to_previous: false };
        }
        return exercise;
    });
    return changed ? result : list;
}

/**
 * Dopo il cambio di set_type dell'esercizio `index`: lo collega a quello sopra e,
 * se serve, collega quello sotto, poi rimuove i collegamenti non più validi.
 * Il collegamento automatico scatta solo qui: se il trainer lo toglie, resta tolto.
 */
export function applySetTypeChangeLinks(list, index) {
    const result = [...list];
    if (result[index]) {
        result[index] = { ...result[index], linked_to_previous: shouldAutoLink(result, index) };
    }
    const nextIndex = index + 1;
    if (result[nextIndex] && !isLinked(result[nextIndex]) && shouldAutoLink(result, nextIndex)) {
        const nextNext = result[nextIndex + 1];
        const nextAlreadyPaired = result[nextIndex].set_type === 'superset' && isLinked(nextNext);
        if (!nextAlreadyPaired) {
            result[nextIndex] = { ...result[nextIndex], linked_to_previous: true };
        }
    }
    return normalizeWorkoutLinks(result);
}

/**
 * Per ogni esercizio restituisce il gruppo collegato di cui fa parte (o null se è da solo):
 * { label: 'Circuit 2', position: 0-based, size, isFirst, isLast, borderClass, barClass, badgeClass }.
 * La numerazione è per tipo, così due circuiti vicini risultano "Circuit 1" e "Circuit 2".
 */
export function computeWorkoutGroups(list) {
    const groups = new Array(list.length).fill(null);
    const countByType = {};
    let start = 0;
    while (start < list.length) {
        let end = start;
        while (end + 1 < list.length && isLinked(list[end + 1])) end += 1;
        const size = end - start + 1;
        if (size > 1) {
            const setType = list[start].set_type;
            countByType[setType] = (countByType[setType] || 0) + 1;
            const label = `${getWorkoutSetTypeLabel(setType)} ${countByType[setType]}`;
            for (let i = start; i <= end; i += 1) {
                groups[i] = {
                    label,
                    position: i - start,
                    size,
                    isFirst: i === start,
                    isLast: i === end,
                    borderClass: GROUP_BORDER_CLASS_BY_TYPE[setType] ?? 'border-l-gray-400',
                    barClass: GROUP_BAR_CLASS_BY_TYPE[setType] ?? 'bg-gray-400',
                    badgeClass: getWorkoutSetTypeBadgeClass(setType),
                };
            }
        }
        start = end + 1;
    }
    return groups;
}

/**
 * In un gruppo collegato il recupero conta solo sull'ultimo esercizio (pausa a fine giro):
 * se il successivo è collegato a questo, qui il recupero non serve.
 */
export function isRecoveryHiddenInGroup(group) {
    return Boolean(group) && !group.isLast;
}
